import PropTypes from 'prop-types';
import { memo, useEffect, useLayoutEffect, useState } from 'react';
import { useSelector, useDispatch } from 'store';
import moment from 'moment';
import { isDate } from 'lodash';
import { isValid } from 'date-fns';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Card, CardContent, Grid, LinearProgress, Button, Typography, linearProgressClasses, Skeleton, Box } from '@mui/material';

import { getMonthlyInteractions, getGoalPeriodProviderCareStats } from 'store/slices/account';
import { humanFormattedDecimalHours } from 'utils/humanSecondsFormatter';
import { getCareStatsRange, setCareStatsRange } from 'utils/careStatsRangePersistence';
import useAuth from 'hooks/useAuth';
import { DesktopDateRangePicker, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import useCommitWhenDone from 'ui-component/pickers/useCommitWhenDone';

// the modes are sent to the server verbatim as the goal period
const RANGE_MODE_DAY = 'day';
const RANGE_MODE_WEEK = 'week';
const RANGE_MODE_MONTH = 'month';
// custom also sends the inclusive start / end dates (SYN-388)
const RANGE_MODE_CUSTOM = 'custom';

const RANGE_MODE_OPTIONS = [
    { mode: RANGE_MODE_DAY, label: 'Daily' },
    { mode: RANGE_MODE_WEEK, label: 'Weekly' },
    { mode: RANGE_MODE_MONTH, label: 'Monthly' },
    { mode: RANGE_MODE_CUSTOM, label: 'Custom' }
];

const RANGE_MODES = RANGE_MODE_OPTIONS.map((option) => option.mode);

const PERIOD_COPY = {
    [RANGE_MODE_DAY]: 'today',
    [RANGE_MODE_WEEK]: 'this week',
    [RANGE_MODE_MONTH]: 'this month'
};

const INVALID_RANGE_COPY = 'End date must be on or after the start date.';
// mirrors the server's MAX_CUSTOM_RANGE_DAYS cap on the progress route
const MAX_CUSTOM_RANGE_DAYS = 366;
const TOO_LONG_RANGE_COPY = `The date range cannot be longer than ${MAX_CUSTOM_RANGE_DAYS} days.`;
const LOAD_FAILED_COPY = 'Could not load your logged care.';

// the custom bounds are plain calendar dates; the server places them in the org zone
const ISO_DATE_FORMAT = 'YYYY-MM-DD';

const toIsoDate = (date) => moment(date).format(ISO_DATE_FORMAT);
// local midnight, so the picker shows the stored calendar day in every browser zone
const fromIsoDate = (isoDate) => moment(isoDate, ISO_DATE_FORMAT).toDate();

const isValidPickerDate = (date) => isDate(date) && isValid(date);

// ISO dates compare as strings
const isEndBeforeStart = ({ startDate, endDate }) => endDate < startDate;

// inclusive day count, so Jan 1 → Dec 31 of a leap year is exactly the cap
const isRangeTooLong = ({ startDate, endDate }) =>
    moment(endDate, ISO_DATE_FORMAT).diff(moment(startDate, ISO_DATE_FORMAT), 'days') + 1 > MAX_CUSTOM_RANGE_DAYS;

// the message to show under the end field, or undefined when the range can be sent
function getCustomRangeError(range) {
    if (isEndBeforeStart(range)) {
        return INVALID_RANGE_COPY;
    }
    if (isRangeTooLong(range)) {
        return TOO_LONG_RANGE_COPY;
    }
    return undefined;
}

// first of the current month through today
function getDefaultCustomRange() {
    return {
        startDate: toIsoDate(moment().startOf('month')),
        endDate: toIsoDate(moment())
    };
}

// the last selection this user made, or Weekly over the default custom range
function getInitialSelection(userId) {
    const stored = getCareStatsRange(userId);
    const defaults = getDefaultCustomRange();
    const storedModeIsKnown = RANGE_MODES.includes(stored?.mode);
    return {
        mode: storedModeIsKnown ? stored.mode : RANGE_MODE_WEEK,
        customRange: {
            startDate: stored?.startDate ?? defaults.startDate,
            endDate: stored?.endDate ?? defaults.endDate
        }
    };
}

// "Sep 1 to Sep 17, 2026", or both years when the range crosses one
function formatCustomRange({ startDate, endDate }) {
    const start = moment(startDate, ISO_DATE_FORMAT);
    const end = moment(endDate, ISO_DATE_FORMAT);
    const startFormat = start.year() === end.year() ? 'MMM D' : 'MMM D, YYYY';
    return `${start.format(startFormat)} to ${end.format('MMM D, YYYY')}`;
}

function getCompletedCopy(rangeMode, customRange, secondsLogged) {
    const hours = humanFormattedDecimalHours(secondsLogged);
    if (rangeMode === RANGE_MODE_CUSTOM) {
        return `You have completed ${hours} from ${formatCustomRange(customRange)}.`;
    }
    return `You have completed ${hours} ${PERIOD_COPY[rangeMode]}.`;
}

// styles
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 30,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.light : '#fff'
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main
    }
}));

const CardStyle = styled(Card)(({ theme }) => ({
    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
    marginBottom: '22px',
    overflow: 'hidden',
    position: 'relative'
}));

/*
2 boxes --> logged care time for the selected range (today / this week / this month / custom dates),
with a progress bar against the period's summed per-day goal when one is configured
the box below is just monthly number of touches
ORG LEVEL
*/

// ==============================|| PROGRESS BAR WITH LABEL ||============================== //

function LinearProgressWithLabel({ targetHours, hoursComplete, value, testId, ...others }) {
    const theme = useTheme();

    return (
        <Grid container direction="column" spacing={1} sx={{ mt: 1.5 }} data-testid={testId}>
            <Grid item>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Typography
                            variant="caption"
                            sx={{ color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800] }}
                        >
                            {/* Care session time logged <br /> */}
                            {hoursComplete} / {`${targetHours.toFixed(2)}`} hours
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="caption" color="inherit">{`${Math.round(value)}%`}</Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <BorderLinearProgress variant="determinate" value={value > 100 ? 100 : value} {...others} />
            </Grid>
        </Grid>
    );
}

LinearProgressWithLabel.propTypes = {
    value: PropTypes.number.isRequired,
    hoursComplete: PropTypes.number.isRequired,
    targetHours: PropTypes.number.isRequired,
    testId: PropTypes.string
};

// ==============================|| PROGRESS BAR SKELETON ||============================== //

function CareStatsProgressSkeleton() {
    return (
        <Grid container direction="column" spacing={1} sx={{ mt: 1.5 }}>
            <Grid item>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton variant="text" width={90} />
                    </Grid>
                    <Grid item>
                        <Skeleton variant="text" width={30} />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <Skeleton variant="rounded" height={10} sx={{ borderRadius: 30 }} />
            </Grid>
        </Grid>
    );
}

// ==============================|| CUSTOM RANGE PICKER ||============================== //

// the sectioned date fields have an intrinsic width wider than the sidebar card, so every
// layer between the wrapper and the sections is told it may shrink to the card
const CUSTOM_RANGE_PICKER_SX = {
    minWidth: 0,
    '& .MuiFormControl-root, & .MuiPickersTextField-root': { width: '100%', minWidth: 0 },
    '& .MuiPickersInputBase-root': { minWidth: 0 },
    '& .MuiPickersInputBase-sectionsContainer': { minWidth: 0, flexShrink: 1 },
    '& .MuiInputAdornment-root': { marginLeft: 0 },
    '& .MuiIconButton-root': { padding: 0.5 }
};

const RANGE_LABELS = { start: 'Start date', end: 'End date' };

const toPickerRange = ({ startDate, endDate }) => [fromIsoDate(startDate), fromIsoDate(endDate)];

// both bounds are required: a half-typed date is never sent
const isCommittableRange = (range) => isValidPickerDate(range?.[0]) && isValidPickerDate(range?.[1]);

// The custom range as one picker: both fields share a calendar popover and the typed value is
// held locally so the fields follow the keystrokes. The parent only hears a committed range
// (calendar selection, or focus leaving the picker) with both dates valid.
function CustomRangePicker({ value, onAccept, errorMessage }) {
    const [draftRange, setDraftRange] = useState(() => toPickerRange(value));

    useEffect(() => {
        setDraftRange(toPickerRange(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.startDate, value.endDate]);

    const { handleAccept, handleBlur } = useCommitWhenDone({
        onAccept: (range) => onAccept({ startDate: toIsoDate(range[0]), endDate: toIsoDate(range[1]) }),
        isCommittable: isCommittableRange
    });

    // the start / end fields get their own test ids; the range message sits under the end field
    const textFieldProps = ({ position }) => ({
        'data-testid': `care-stats-${position}-date`,
        size: 'small',
        error: Boolean(errorMessage),
        helperText: position === 'end' ? errorMessage : undefined
    });

    return (
        <Box onBlur={handleBlur} data-testid="care-stats-custom-range" sx={CUSTOM_RANGE_PICKER_SX}>
            <DesktopDateRangePicker
                value={draftRange}
                onChange={setDraftRange}
                onAccept={handleAccept}
                localeText={RANGE_LABELS}
                slots={{ field: MultiInputDateRangeField }}
                slotProps={{
                    // stacked so both fields fit the sidebar; the separator between them is noise there
                    field: { direction: 'column', spacing: 1.5 },
                    separator: { sx: { display: 'none' } },
                    textField: textFieldProps
                }}
            />
        </Box>
    );
}

CustomRangePicker.propTypes = {
    value: PropTypes.shape({ startDate: PropTypes.string.isRequired, endDate: PropTypes.string.isRequired }).isRequired,
    onAccept: PropTypes.func.isRequired,
    errorMessage: PropTypes.string
};

// ==============================|| SIDEBAR - MENU CARD ||============================== //

const TodayCareStatsCards = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    // the auth context carries the user before the account slice is populated
    const { userId } = useAuth();

    const [rangeMode, setRangeMode] = useState(() => getInitialSelection(userId).mode);
    const [customRange, setCustomRange] = useState(() => getInitialSelection(userId).customRange);
    const organizationMeta = useSelector((state) => state.account.organizationMeta);
    const accountData = useSelector((state) => state.account);

    const isCustomMode = rangeMode === RANGE_MODE_CUSTOM;
    const customRangeError = isCustomMode ? getCustomRangeError(customRange) : undefined;
    const customRangeInvalid = Boolean(customRangeError);

    function refreshCareStatsForActiveRange() {
        if (customRangeInvalid) {
            return;
        }
        // day / week / month windows and the matching goal are resolved by the server (Eastern);
        // custom sends its inclusive bounds and the server sums the day goals the same way
        dispatch(getGoalPeriodProviderCareStats(rangeMode, isCustomMode ? customRange : undefined));
    }

    useEffect(() => {
        dispatch(getMonthlyInteractions());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationMeta]);

    // useLayoutEffect so the loading state is dispatched before the browser paints —
    // otherwise switching ranges flashes the previous range's data (still loaded) for one frame
    useLayoutEffect(() => {
        refreshCareStatsForActiveRange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rangeMode, customRange.startDate, customRange.endDate, organizationMeta]);

    // the selection persists per user between visits (SYN-388)
    useEffect(() => {
        setCareStatsRange(userId, { mode: rangeMode, ...customRange });
    }, [userId, rangeMode, customRange]);

    const handleCustomRangeAccepted = (range) => {
        setCustomRange(range);
    };

    useEffect(() => {
        if (accountData.customRangeProviderCareStats.needsRefresh) {
            refreshCareStatsForActiveRange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountData.customRangeProviderCareStats.needsRefresh]);

    const careStats = accountData.customRangeProviderCareStats;
    const patientsCaredFor = accountData.monthlyInteractions.month.patientsCaredFor;
    const goalHours = careStats.custom.goalHours;
    // no goal configured for the period → hours only, no progress bar (D4)
    const hasGoalForPeriod = goalHours > 0;
    const hoursComplete = Math.round((careStats.custom.secondsLogged / 60 / 60) * 100) / 100;
    const percentageComplete = hasGoalForPeriod ? Math.round((hoursComplete / goalHours) * 100) : 0;
    const completedCopy = getCompletedCopy(rangeMode, customRange, careStats.custom.secondsLogged);
    // an invalid custom range is never fetched, so the stats shown would belong to the previous range
    const showStats = !customRangeInvalid;
    const loadFailed = careStats.loaded && Boolean(careStats.error);

    const textSx = { opacity: theme.palette.mode === 'dark' ? 1 : 0.6, marginTop: 1 };
    const textColor = theme.palette.mode === 'dark' ? 'textSecondary' : 'grey.900';

    return (
        <>
            <CardStyle data-testid="care-stats-widget">
                <CardContent sx={{ p: 2 }}>
                    <Grid item>
                        <Typography
                            variant="h4"
                            sx={{
                                color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800]
                            }}
                        >
                            My logged care
                        </Typography>
                    </Grid>
                    {/* two per row so the four modes read as a deliberate 2 × 2 grid in the narrow sidebar */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', justifyContent: 'start', marginTop: 1, marginBottom: 1 }}>
                        {RANGE_MODE_OPTIONS.map((option) => (
                            <Button
                                key={option.mode}
                                disableElevation
                                variant={rangeMode === option.mode ? 'contained' : 'text'}
                                size="small"
                                data-testid={`care-stats-mode-${option.mode}`}
                                sx={{ color: 'inherit', minWidth: 0, px: 1, '&.MuiButton-contained': { color: '#FFFFFF' } }}
                                onClick={() => setRangeMode(option.mode)}
                            >
                                {option.label}
                            </Button>
                        ))}
                    </Box>
                    {isCustomMode && (
                        // the floating labels rise above the fields, so extra top margin keeps the pickers as far from the buttons as from the bar below
                        <Box sx={{ mt: 2 }}>
                            <CustomRangePicker value={customRange} onAccept={handleCustomRangeAccepted} errorMessage={customRangeError} />
                        </Box>
                    )}
                    {showStats && loadFailed && (
                        <Typography variant="subtitle1" color="error" sx={{ marginTop: 1 }} data-testid="care-stats-error">
                            {LOAD_FAILED_COPY}
                        </Typography>
                    )}
                    {showStats && careStats.loaded && !loadFailed && hasGoalForPeriod && (
                        <LinearProgressWithLabel
                            testId="care-stats-progress"
                            targetHours={goalHours}
                            hoursComplete={hoursComplete}
                            value={percentageComplete}
                        />
                    )}
                    {showStats && !careStats.loaded && <CareStatsProgressSkeleton />}
                    {showStats && !loadFailed && (
                        <Grid item>
                            {careStats.loaded ? (
                                <Typography variant="subtitle1" color={textColor} sx={textSx} data-testid="care-stats-completed">
                                    {completedCopy}
                                </Typography>
                            ) : (
                                <>
                                    <Skeleton variant="text" sx={{ marginTop: 1 }} />
                                    <Skeleton variant="text" width="60%" />
                                </>
                            )}
                        </Grid>
                    )}
                </CardContent>
            </CardStyle>
            {accountData.monthlyInteractions.loaded && (
                <CardStyle>
                    <CardContent sx={{ p: 2 }}>
                        <Grid item>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800]
                                }}
                            >
                                Monthly unique <br /> patient interactions
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="subtitle1" color={textColor} sx={textSx}>
                                You have cared for {patientsCaredFor} patients so far this month.
                            </Typography>
                        </Grid>
                    </CardContent>
                </CardStyle>
            )}
        </>
    );
};

export default memo(TodayCareStatsCards);
